import './init';

import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router/dom';
import {
    MutationCache,
    QueryCache,
    QueryClient,
    QueryClientProvider,
    QueryMeta,
} from '@tanstack/react-query';
import './scss/app.scss';
import { toast, ToastContainer } from 'react-toastify';
import { FadeInDown } from '@utils/toastAnimations';
import './js/i18n';
import { MsalProvider } from '@auth';
import router from '@routing/routes';
import ThemeProvider from '@components/features/themeProvider/ThemeProvider';
import { Suspense } from 'react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { msalInstance } from '@js/msalInstance';
import { seedMockAuth } from '@auth/mockAuth';

// Dev-only: seed a fake account + token (no-op unless MOCK_AUTH=true).
seedMockAuth();

const onGlobalQueryError = (error: any, meta?: QueryMeta) => {
    if (meta?.disableErrorToaster) {
        return;
    }

    const errorMessage =
        error?.response?.data?.message || error?.response?.data?.Message;
    if (errorMessage) {
        toast.error(errorMessage);
    }
};

const queryClient = new QueryClient({
    queryCache: new QueryCache({
        onError: (error, query) => onGlobalQueryError(error, query.meta),
    }),
    mutationCache: new MutationCache({
        onError: (error, variables, context, mutation) =>
            onGlobalQueryError(error, mutation.options.meta),
    }),
});

const container = document.getElementById('root');
const root = createRoot(container!);

root.render(
    <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
            <ThemeProvider>
                <Suspense>
                    <RouterProvider router={router} />
                    <ToastContainer
                        position="bottom-right"
                        autoClose={3000}
                        transition={FadeInDown}
                        theme="dark"
                        className="toastContainer"
                    />
                </Suspense>
            </ThemeProvider>
            <ReactQueryDevtools />
        </QueryClientProvider>
    </MsalProvider>,
);
